import React, { useRef, useState, ChangeEvent } from 'react';
import { formatPhoneBR, onlyDigits } from '../../../../utils/phoneMask';

import { type OrganizadorFuncao } from '../../../../validation/tournamentOrganizerSchema';

export type TournamentOrganizer = {
  id?: string;
  nome: string;
  telefone?: string | null;
  fotoUrl?: string | null;
  nomeFuncao?: string | null;
};

export const ORGANIZADOR_FUNCOES: { value: OrganizadorFuncao; label: string }[] = [
  { value: 'ADMINISTRADOR', label: 'Administrador' },
  { value: 'FINANCEIRO', label: 'Financeiro' },
  { value: 'GERENTE', label: 'Gerente' },
  { value: 'SOCIAL', label: 'Social' },
];

type Props = {
  value: TournamentOrganizer;
  onChange: (patch: Partial<TournamentOrganizer>) => void;
  onUploadPhoto?: (file: File) => Promise<string>;
  onDelete?: () => void;
  busy?: boolean;
  errors?: { nome?: string; telefone?: string };
  readOnly?: boolean;
  className?: string;
};

const TournamentOrganizerItem: React.FC<Props> = ({
  value,
  onChange,
  onUploadPhoto,
  onDelete,
  busy = false,
  errors,
  readOnly = false,
  className,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [hover, setHover] = useState(false);
  const [uploading, setUploading] = useState(false);
  const disabled = readOnly || busy || uploading;

  const handleField = (field: keyof TournamentOrganizer) => (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    let { value } = e.target;

    if (field === 'telefone') {
      value = formatPhoneBR(value);
    }

    if (field === 'nome' && value.length > 100) {
      value = value.slice(0, 100);
    }

    onChange({ [field]: value || null });
  };

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (onUploadPhoto) {
      setUploading(true);
      try {
        const url = await onUploadPhoto(file);
        onChange({ fotoUrl: url });
      } catch (err) {
        console.error(err);
      } finally {
        setUploading(false);
      }
    } else {
      const preview = URL.createObjectURL(file);
      onChange({ fotoUrl: preview });
    }
  };

  const imgSrc = value.fotoUrl || '/assets/images/avatar.jpg';

  return (
    <div
      className={`card p-3 position-relative ${className || ''} ${disabled ? 'opacity-75' : ''}`}
      aria-busy={uploading || busy}
    >
      {(uploading || busy) && (
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-50 d-flex align-items-center justify-content-center rounded">
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}

      <div className="d-flex justify-content-end mb-3">
        <button
          type="button"
          className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-2 ms-auto"
          onClick={() => onDelete?.()}
          disabled={disabled}
          aria-label="Remover organizador"
          title="Remover organizador"
        >
          <i className="bi  bi-trash" aria-hidden="true" />
        </button>
      </div>

      <div className="d-flex flex-column flex-md-row gap-3">
        <div
          className="position-relative"
          style={{ width: 120, height: 120, flex: '0 0 auto' }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <img
            src={imgSrc}
            alt={`Foto do organizador ${value.nome || ''}`}
            className="w-100 h-100 rounded"
            style={{ objectFit: 'cover' }}
          />
          {!readOnly && (
            <>
              <input
                type="file"
                accept="image/*"
                className="d-none"
                ref={fileInputRef}
                onChange={handleFile}
                disabled={disabled}
              />
              {hover && !disabled && (
                <button
                  type="button"
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 border-0 rounded"
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="Alterar foto"
                  title="Alterar foto"
                >
                  <i className="ti-camera text-white fs-4" />
                </button>
              )}
            </>
          )}
        </div>

        <div className="flex-grow-1 w-100">
          <div className="mb-3">
            <label className="form-label">Nome *</label>
            <input
              type="text"
              className={`form-control ${errors?.nome ? 'is-invalid' : ''}`}
              value={value.nome}
              onChange={handleField('nome')}
              disabled={disabled}
              required
              minLength={2}
              maxLength={50}
              readOnly={readOnly}
            />
            {errors?.nome && <div className="invalid-feedback">{errors.nome}</div>}
          </div>
          <div className="mb-3">
            <label className="form-label">Telefone</label>
            <input
              type="tel"
              inputMode="tel"
              className={`form-control ${errors?.telefone ? 'is-invalid' : ''}`}
              value={value.telefone || ''}
              onChange={handleField('telefone')}
              onBlur={(e) => {
                const digits = onlyDigits(e.target.value);
                const masked = formatPhoneBR(digits);
                if (masked !== e.target.value) onChange({ telefone: masked });
              }}
              disabled={disabled}
              readOnly={readOnly}
              placeholder="(99) 99999-9999"
            />
            {errors?.telefone && (
              <div className="invalid-feedback">{errors.telefone}</div>
            )}
          </div>
          <div className="mb-3">
            <label className="form-label">Função</label>
            <select
              className="form-control"
              value={value.nomeFuncao || ''}
              onChange={handleField('nomeFuncao')}
              disabled={disabled}
            >
              <option value="">Selecione uma função</option>
              {ORGANIZADOR_FUNCOES.map((funcao) => (
                <option key={funcao.value} value={funcao.value}>
                  {funcao.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentOrganizerItem;
